import { createLayeredPreferencesStore, createLocalPreferencesStore, createMemoryPreferencesStore, type PreferencesStore } from '@zagru/design-system';
import type { ApiClient } from '@zagru/platform';

/** The `scope` every shell preference (theme, density, locale, units, sidebar) is stored under on the server. */
export const UI_PREFERENCES_SCOPE = 'ui';

interface PreferenceValue {
  key: string;
  value: string;
  fromTenantDefault: boolean;
}

function parse<T>(raw: string | undefined): T | undefined {
  if (raw === undefined) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

/**
 * The server half of the preferences store (D-009): `platform/preferences` under one scope, read once and
 * kept in memory, written through on every change. There is no push channel for preferences, so
 * `subscribe` is silent; the local mirror is what answers synchronously and across tabs.
 */
export function createServerPreferencesStore(client: ApiClient, scope = UI_PREFERENCES_SCOPE): PreferencesStore {
  let loaded: Promise<Map<string, string>> | null = null;
  const load = () => {
    loaded ??= client
      .get<Record<string, PreferenceValue>>('/api/v1/platform/preferences', { query: { scope } })
      .then((map) => new Map(Object.values(map ?? {}).map((entry) => [entry.key, entry.value])))
      .catch(() => {
        // A preferences read that fails must never break the screen; the mirror carries on and the next
        // read tries the server again.
        loaded = null;
        return new Map<string, string>();
      });
    return loaded;
  };
  return {
    get: async <T,>(key: string) => parse<T>((await load()).get(key)),
    set: async (key, value) => {
      const serialized = JSON.stringify(value);
      (await load()).set(key, serialized);
      try {
        await client.put(`/api/v1/platform/preferences/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`, { value: serialized });
      } catch {
        // The mirror already holds the choice; the server catches up on the next write.
      }
    },
    remove: async (key) => {
      (await load()).delete(key);
      try {
        await client.delete(`/api/v1/platform/preferences/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`);
      } catch {
        // See `set`.
      }
    },
    subscribe: () => () => {},
  };
}

/**
 * The app's store: the server as the source of truth once a session exists, mirrored to `localStorage`
 * under a user-scoped namespace so a preference never leaks across users on a shared browser and never
 * waits on the network. Before sign-in (or in a signed-out shell) it is a memory store.
 */
export function createAppPreferencesStore(client: ApiClient | null, userId: string | null | undefined): PreferencesStore {
  if (!userId) return createMemoryPreferencesStore();
  const mirror = createLocalPreferencesStore({ namespace: `zagru.${userId}.prefs` });
  return client ? createLayeredPreferencesStore(createServerPreferencesStore(client), mirror) : mirror;
}
